import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DynamicTable from '../shared/dynamicTable/dynamicTable';
import CloseIcon from '@mui/icons-material/Close';
import { GridContainer, GridItem, PaddedCard } from '../shared/ui-extension';

/**
 * Dialog component displaying active quotes for an item over the last 3 months.
 * Shows a table of quotes and summary statistics (average cost, price, margin).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.activeQuotesOpen - Whether the dialog is open.
 * @param {function} props.setActiveQuotesOpen - Function to set dialog open state.
 * @param {string|number} props.itemId - The item ID for which quotes are shown.
 * @param {Array<Object>} props.activeQuotes - Array of active quote objects.
 * @returns {JSX.Element}
 */
function ActiveQuotesDialog({
  activeQuotesOpen,
  setActiveQuotesOpen,
  itemId,
  activeQuotes,
}) {
  // Filter quotes to include only those with unitPrice > 0
  const activeQuotesWithUnitPrice = activeQuotes.filter(
    (quote) => +quote.unitPrice.split('$')[1] > 0
  );

  /**
   * Calculates the average total cost from active quotes.
   * Only includes quotes with unitPrice > 0.
   * @type {string}
   */
  const averageTotalCost = (
    activeQuotesWithUnitPrice.reduce((acc, quote) => {
      acc += +quote.totalCost.split('$')[1];
      return acc;
    }, 0) / (activeQuotesWithUnitPrice.length || 1)
  ).toFixed(5);

  /**
   * Calculates the average unit price from active quotes.
   * Only includes quotes with unitPrice > 0.
   * @type {string}
   */
  const averageUnitPrice = (
    activeQuotesWithUnitPrice.reduce((acc, quote) => {
      acc += +quote.unitPrice.split('$')[1];
      return acc;
    }, 0) / (activeQuotesWithUnitPrice.length || 1)
  ).toFixed(5);

  return (
    <Dialog
      open={activeQuotesOpen}
      maxWidth='xl'
      onClose={() =>
        // This view is read-only, so closing on a backdrop click can't discard
        // any user changes.
        setActiveQuotesOpen(false)
      }
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id='customized-dialog-title'>
        Last 3 Months Active Quotes for {itemId}
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={() => setActiveQuotesOpen(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <GridContainer justifyContent='flex-end'>
          <GridItem xs={12} sx={{ marginBottom: 2 }}>
            <DynamicTable
              showFilter={true}
              rowFilterLayout={true}
              tableCompactRows={true}
              rows={activeQuotes}
              filterFields={['id']}
              showSort={true}
              tableHeight={550}
            />
          </GridItem>
          <GridItem xs={4}>
            <PaddedCard>
              <Typography align='center' variant='h6' gutterBottom={true}>
                Summary from {activeQuotes.length} quotes
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align='right'>Average Total Cost</TableCell>
                    <TableCell align='right'>Average Unit Price</TableCell>
                    <TableCell align='right'>Average Margin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align='right'>
                      {`$ ${averageTotalCost}`}
                    </TableCell>
                    <TableCell align='right'>
                      {`$ ${averageUnitPrice}`}
                    </TableCell>
                    <TableCell align='right'>
                      {`${
                        averageUnitPrice > 0 && averageTotalCost > 0
                          ? (
                              ((averageUnitPrice - averageTotalCost) /
                                averageUnitPrice) *
                              100
                            ).toFixed(2)
                          : 0
                      } %`}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </PaddedCard>
          </GridItem>
        </GridContainer>
      </DialogContent>
    </Dialog>
  );
}

export default ActiveQuotesDialog;
